import { useAppSelector } from "@/store/store-hooks";
import { selectTags } from "@/store/tags/tags-selectors";
import {
  FaroText,
  FilterMenuOptionType,
  NO_TRANSLATE_CLASS,
} from "@faro-lotv/flat-ui";
import { GUID } from "@faro-lotv/foundation";
import { useMemo, type JSX } from "react";
import { ChipFilterWithSearch } from "./chip-filter-with-search";

export type AnnotationTagFilterProps = {
  /** List of selected tag IDs */
  selectedTags: GUID[];

  /** Callback executed when the selection changes */
  onSelectedTagsChanged(ids: GUID[]): void;
};

/** @returns A chip with a popup that can be used to select a list of tags */
export function TagFilter({
  selectedTags,
  onSelectedTagsChanged,
}: AnnotationTagFilterProps): JSX.Element {
  const tags = useAppSelector(selectTags);

  const options = useMemo<FilterMenuOptionType[]>(
    () =>
      tags.map((t) => ({
        key: t.id,
        value: t.name,
        label: (
          <FaroText variant="bodyS" className={NO_TRANSLATE_CLASS}>
            {t.name}
          </FaroText>
        ),
        tooltip: (
          <FaroText variant="bodyS" className={NO_TRANSLATE_CLASS} dark>
            {t.name}
          </FaroText>
        ),
      })),
    [tags],
  );

  const selectedOptions = useMemo<FilterMenuOptionType[]>(
    () =>
      selectedTags
        .map((tagId) => options.find((option) => option.key === tagId))
        .filter((option) => !!option),
    [selectedTags, options],
  );

  return (
    <ChipFilterWithSearch
      label="Tags"
      selectedOptions={selectedOptions}
      onFilterChange={(selected) =>
        onSelectedTagsChanged(selected.map((t) => t.key))
      }
      options={options}
      headingLabel="Select tags"
    />
  );
}
